import React, {CSSProperties, FC, ReactElement, useLayoutEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import ContextMenuItem, {ContextMenuItemProps} from './ContextMenuItem';

import './ContextMenu.less';
import {ClickAwayListener} from "@mui/material";
import {popupContainerFor} from "../Portal/popupContainer";

export type ContextMenuProps = {
    children: ReactElement,
    items: ContextMenuItemProps[],
    yOffset?: number,
};

const ContextMenu: FC<ContextMenuProps> = props => {
    const [open, setOpen] = useState(false);
    const [x, setX] = useState(0);
    const [y, setY] = useState(0);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const [menuWidth, setMenuWidth] = useState(0);
    const [menuHeight, setMenuHeight] = useState(0);
    // Where the open menu is portaled: inside the enclosing MUI modal, or #portal-root; see popupContainerFor (OWA-955)
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

    const saveRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (open) {
            const ref = portalRef.current || saveRef.current;
            if (!ref) return;

            const {clientWidth} = ref;
            const {clientHeight} = saveRef.current;

            if (clientWidth !== menuWidth || clientHeight !== menuHeight) {
                setMenuWidth(clientWidth);
                setMenuHeight(clientHeight);
            }
        }
    }, [menuHeight, menuWidth, open]);

    const onRightClick = (x: React.SetStateAction<number>, y: React.SetStateAction<number>, anchor: Element) => {
        setX(x);
        setY(y);
        setWidth(window.innerWidth);
        setHeight(window.innerHeight);
        setPortalContainer(popupContainerFor(anchor, document.getElementById('portal-root')));
        setOpen(true);
    };

    const afterClick = () => {
        setOpen(false);
    };

    const pageWidth = width;
    const pageHeight = height;

    // TODO: Why is this not just menuHeight?
    // const menuHeight = props.items.length * 35; // Making some pretty big assumptions here...

    const locationInfo = {x, y, pageHeight, pageWidth, menuHeight, menuWidth};

    const style: CSSProperties = {};

    // Should the mouse pointer be the left or right side of the popup?
    if (pageWidth - menuWidth < x) style.left = pageWidth - menuWidth;
    else style.left = x;

    // Same with the height
    if (pageHeight - menuHeight < y) {
        style.top = pageHeight - menuHeight;
    }
    else style.top = y;

    const menu = props.items.map((item, index) => (
        <ContextMenuItem key={item.label}
                         icon={item.icon}
                         onClick={item.onClick}
                         showIf={item.showIf}
                         label={item.label}
                         afterClick={afterClick}
                         position={index}
                         totalItems={props.items.length}
                         locationInfo={locationInfo}
                         subItems={item.subItems}
        />));

    return <ClickAwayListener onClickAway={() => setOpen(false)} mouseEvent='onMouseDown' touchEvent='onTouchStart'>
        <div ref={saveRef} className="ContextMenu-Wrapper">
            {React.cloneElement(props.children, {
                onContextMenu: (event: React.MouseEvent) => {
                    event.preventDefault();
                    // The menu is position: fixed, so it takes viewport coordinates (OWA-955).
                    onRightClick(event.clientX, event.clientY, event.currentTarget);
                }
            } as Partial<React.DOMAttributes<Element>>)}
            {open &&
                ReactDOM.createPortal(
                    <div className="ContextMenu"
                         id="popupMenu"
                         style={style}
                         ref={portalRef}
                    >
                        {menu}
                    </div>, portalContainer ?? document.getElementById('portal-root'))
            }
        </div>
    </ClickAwayListener>;
}

export default ContextMenu;
